import type { Feature, FeatureCollection } from 'geojson'

type Pt = [number, number]

/** Flatten every coordinate pair out of a Polygon/MultiPolygon/Point-ish geometry. */
function collectCoords(feature: Feature, out: Pt[]): void {
  const geom = feature.geometry
  if (!geom || !('coordinates' in geom)) return
  const walk = (coords: unknown): void => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number') {
      const [x, y] = coords as number[]
      out.push([x, y])
      return
    }
    for (const child of coords) walk(child)
  }
  walk(geom.coordinates)
}

/** 2D cross product of OA × OB for the Andrew monotone-chain turn test. */
function cross(o: Pt, a: Pt, b: Pt): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
}

/**
 * Convex hull (Andrew's monotone chain) of every vertex across the given features,
 * serialized as a closed WKT `POLYGON((...))` ring. Returns '' when there are no
 * usable coordinates. Used to turn the top-5 tornado precincts into a single polygon
 * scope for the analytics create-turf / export endpoints.
 */
export function convexHullWkt(features: Feature[]): string {
  const pts: Pt[] = []
  for (const f of features) collectCoords(f, pts)
  if (pts.length === 0) return ''

  // De-dup + sort by x then y.
  const sorted = Array.from(new Map(pts.map((p) => [`${p[0]},${p[1]}`, p])).values()).sort(
    (a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]),
  )
  if (sorted.length < 3) {
    // Degenerate — echo the points back as a (possibly collinear) ring so callers get valid WKT.
    const ring = [...sorted, sorted[0]]
    return `POLYGON((${ring.map((p) => `${p[0]} ${p[1]}`).join(', ')}))`
  }

  const lower: Pt[] = []
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop()
    }
    lower.push(p)
  }
  const upper: Pt[] = []
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop()
    }
    upper.push(p)
  }
  // Drop each chain's last point (shared with the other chain's first).
  const hull = [...lower.slice(0, -1), ...upper.slice(0, -1)]
  const ring = [...hull, hull[0]] // close the ring
  return `POLYGON((${ring.map((p) => `${p[0]} ${p[1]}`).join(', ')}))`
}

/**
 * The subset of a choropleth FeatureCollection to fit the map camera to: only features that
 * carry a value (`properties.hasValue`, as stamped by `buildChoroplethGeoJson`). The precinct
 * fetch is state-wide, so fitting to every feature framed all of the state even for a single
 * district contest; the valued features ARE the contest's precincts. Falls back to the full
 * collection when nothing carries a value (so the camera still lands somewhere sensible).
 */
export function valuedFeaturesForFit(
  fc: FeatureCollection | null | undefined,
): FeatureCollection | null {
  if (!fc) return null
  const valued = fc.features.filter((f) => f.properties?.hasValue === true)
  return valued.length > 0 ? { ...fc, features: valued } : fc
}
